import { useStripe, Elements, PaymentElement, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Palette, MapPin, Trophy, Zap } from 'lucide-react';

if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  throw new Error('Missing required Stripe key: VITE_STRIPE_PUBLIC_KEY');
}
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const SubscribeForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin,
      },
    });

    if (error) {
      toast({
        title: "Payment Failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Welcome to StreetScope Pro!",
        description: "Your subscription is now active. Enjoy unlimited access!",
      });
    }
    setIsLoading(false);
  }

  return (
    <Card className="w-full max-w-md mx-auto border-red-600/20 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20">
      <CardHeader className="text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-red-600 flex items-center justify-center mb-4">
          <Palette className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl font-bold text-red-800 dark:text-red-200">
          StreetScope Pro
        </CardTitle>
        <CardDescription className="text-red-600 dark:text-red-300">
          Unlock premium street art features
        </CardDescription>
        <div className="text-3xl font-bold text-red-700 dark:text-red-300 mt-2">
          $9.99<span className="text-sm font-normal">/month</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <PaymentElement className="p-3 border border-red-200 rounded-lg bg-white/50 dark:bg-gray-800/50" />
          <Button 
            type="submit" 
            disabled={!stripe || isLoading}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3"
            data-testid="button-subscribe"
          >
            {isLoading ? "Processing..." : "Subscribe to Pro"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default function Subscribe() {
  const [clientSecret, setClientSecret] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiRequest("POST", "/api/get-or-create-subscription")
      .then((res) => res.json())
      .then((data) => {
        setClientSecret(data.clientSecret);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Subscription setup error:", error);
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-100 via-orange-100 to-yellow-100 dark:from-red-950 dark:via-orange-950 dark:to-yellow-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mx-auto" />
          <p className="text-red-700 dark:text-red-300">Setting up subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-100 via-orange-100 to-yellow-100 dark:from-red-950 dark:via-orange-950 dark:to-yellow-950 py-12 px-4 relative">
      {/* Navigation */}
      <div className="absolute top-6 left-6">
        <Link to="/">
          <Button variant="ghost" size="sm" className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900">
            ← Back to Home
          </Button>
        </Link>
      </div>
      <div className="absolute top-6 right-6">
        <Button 
          variant="outline" 
          size="sm" 
          className="text-red-600 border-red-400 hover:bg-red-100 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900"
          onClick={() => window.location.href = '/api/logout'}
          data-testid="button-signout-subscribe"
        >
          Sign Out
        </Button>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-red-800 dark:text-red-200 mb-4">
            Upgrade to StreetScope Pro
          </h1>
          <p className="text-lg text-red-600 dark:text-red-300 max-w-2xl mx-auto">
            Join the elite community of street art explorers with premium features and exclusive access to legal graffiti walls worldwide
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Features */}
          <div className="space-y-6">
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
                  <MapPin className="w-5 h-5" />
                  Premium Features
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full" />
                  <span>Access to 300+ legal graffiti walls globally</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full" />
                  <span>Complete Ironlak color chart with 100+ colors</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full" />
                  <span>Artist profiles and exclusive video content</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full" />
                  <span>Priority in community events and contests</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-600 rounded-full" />
                  <span>Advanced filters and map layers</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 dark:border-orange-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                  <Zap className="w-5 h-5" />
                  Bonus Rewards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  <Trophy className="w-3 h-3 mr-1" />
                  2x Token Earnings
                </Badge>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Earn double tokens for every discovery and achievement
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Payment Form */}
          <div>
            {clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <SubscribeForm />
              </Elements>
            ) : (
              <Card className="w-full max-w-md mx-auto">
                <CardContent className="p-8 text-center">
                  <p className="text-red-600 dark:text-red-400">
                    Unable to load payment form. Please try again later.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};